// AppLock LockScreen — unlock form for returning users.

import { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const ok = await onUnlock(password);
      if (!ok) {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch {
      setError('Unlock failed. Please try again.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 p-6"
      style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text)' }}
      data-testid="applock-lock"
    >
      <div className="panel" style={{ maxWidth: '420px', width: '100%' }}>
        <div className="flex flex-col items-center gap-2" style={{ textAlign: 'center' }}>
          <Lock size={36} aria-hidden="true" style={{ color: 'var(--color-accent)' }} />
          <h1 className="text-xl font-bold">App Locked</h1>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Enter your password to unlock Carry Roll.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" style={{ marginTop: 'var(--space-5)' }}>
          <div>
            <label htmlFor="applock-unlock" className="field-label">
              Password
            </label>
            <div className="flex items-center gap-2">
              <input
                id="applock-unlock"
                type={show ? 'text' : 'password'}
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? 'Hide password' : 'Show password'}
                style={{ padding: 'var(--space-2)' }}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p
              className="text-sm"
              style={{ color: 'var(--color-danger)' }}
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password}
            data-testid="applock-unlock-submit"
          >
            {busy ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}
